// StatusAndTransitions — render the status badge + available transition
// buttons + (on Draft) the precondition checklist. Implements:
//   - F-003 409 race recovery (auto-refresh + auto-dismiss; no Retry)
//   - G-09 hidden / disabled / blocking-modal denial
//   - eager-credits-derived `sales_credit_complete` precondition

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { Agreement, Principal } from '@/types/domain';
import {
  canActivateAgreement,
  canAmendAgreement,
  canArchiveAgreement,
  canCompleteAgreement,
  canRevertAgreement,
} from '@/auth/permissions';
import { agreementsApi } from '@/api/endpoints';
import { qk } from '@/api/queryKeys';
import { useToast } from '@/components/ToastHost';
import { ApiError } from '@/types/errors';
import { ConfirmationDialog } from './ConfirmationDialog';
import { ActivationBlockersModal } from './ActivationBlockersModal';
import { ArchiveBlockersModal } from './ArchiveBlockersModal';

type Transition = 'activate' | 'complete' | 'revert' | 'archive';

interface PreconditionState {
  has_assignments: boolean;
  // null = pending (data still loading); true/false = known.
  sales_credit_complete: boolean | null;
  // Only relevant on FP. null = pending; true/false = known.
  payment_schedule_within_tolerance: boolean | null;
}

interface Props {
  agreement: Agreement;
  principal: Principal | null;
  precondition: PreconditionState;
  preconditionError: boolean;
}

export function StatusAndTransitions({
  agreement,
  principal,
  precondition,
  preconditionError,
}: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const isFP = agreement.type === 'fixed_price';

  const [confirmOpen, setConfirmOpen] = useState<Transition | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [stateRaceMessage, setStateRaceMessage] = useState<string | null>(null);
  const [activationError, setActivationError] = useState<ApiError | null>(null);
  const [archiveError, setArchiveError] = useState<ApiError | null>(null);

  const activate = canActivateAgreement(principal, agreement);
  const complete = canCompleteAgreement(principal, agreement);
  const revert = canRevertAgreement(principal, agreement);
  const archive = canArchiveAgreement(principal, agreement);
  const amend = canAmendAgreement(principal, agreement);

  const transitionMutation = useMutation({
    mutationFn: async (t: Transition) => {
      switch (t) {
        case 'activate':
          return agreementsApi.activate(agreement.id);
        case 'complete':
          return agreementsApi.complete(agreement.id);
        case 'revert':
          return agreementsApi.revert(agreement.id);
        case 'archive':
          return agreementsApi.archive(agreement.id);
      }
    },
  });

  const closeAndRefresh = async () => {
    await qc.invalidateQueries({ queryKey: qk.agreement(agreement.id) });
    void qc.invalidateQueries({ queryKey: ['agreements', 'list'] });
    setStateRaceMessage(null);
    setConfirmOpen(null);
  };

  const performTransition = async (t: Transition) => {
    setConfirmError(null);
    setStateRaceMessage(null);
    try {
      await transitionMutation.mutateAsync(t);
      void qc.invalidateQueries({ queryKey: qk.agreement(agreement.id) });
      void qc.invalidateQueries({ queryKey: ['agreements', 'list'] });
      toast.show({
        severity: 'success',
        message:
          t === 'activate'
            ? 'Agreement activated.'
            : t === 'complete'
              ? 'Agreement completed.'
              : t === 'revert'
                ? 'Agreement reverted to Active.'
                : 'Agreement archived.',
      });
      setConfirmOpen(null);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'invalid_state_transition' || err.code === 'irreversible_status') {
          // F-003 race recovery.
          setStateRaceMessage(
            "This agreement's status has already changed. Refreshing the view.",
          );
          // Trigger refresh; on success, dismiss the dialog.
          await closeAndRefresh();
          return;
        }
        if (t === 'activate' && (
          err.code === 'incomplete_sales_credit' ||
          err.code === 'payment_schedule_mismatch' ||
          err.code === 'no_assignments' ||
          err.code === 'activation_blocked'
        )) {
          setActivationError(err);
          setConfirmOpen(null);
          return;
        }
        if (t === 'archive' && err.code === 'active_assignments_exist') {
          setArchiveError(err);
          setConfirmOpen(null);
          return;
        }
        setConfirmError(err.message ?? 'The action failed. Please try again.');
        return;
      }
      setConfirmError('The action failed. Please try again.');
    }
  };

  // Activate is a no-confirm path; clicking the button submits directly.
  const handleActivateClick = async () => {
    setActivationError(null);
    setStateRaceMessage(null);
    await performTransition('activate');
  };

  const activatePreconditionsKnown =
    precondition.sales_credit_complete !== null &&
    (!isFP || precondition.payment_schedule_within_tolerance !== null);
  const activateBlocked =
    !precondition.has_assignments ||
    precondition.sales_credit_complete === false ||
    (isFP && precondition.payment_schedule_within_tolerance === false);
  const activateTooltip = !activatePreconditionsKnown
    ? 'Computing requirements…'
    : !precondition.has_assignments
      ? 'No assignments yet — add an assignment first.'
      : precondition.sales_credit_complete === false
        ? 'Sales Credit is not yet 100% on every assignment.'
        : isFP && precondition.payment_schedule_within_tolerance === false
          ? 'Payment schedule total is more than $100 from Total Value.'
          : undefined;

  return (
    <section className="section" aria-labelledby="status-transitions-h2">
      <div className="section-header">
        <h2 id="status-transitions-h2">Status &amp; transitions</h2>
      </div>

      {stateRaceMessage ? (
        <div className="inline-error inline-error-race" role="status" aria-live="polite">
          {stateRaceMessage}
        </div>
      ) : null}

      {agreement.status === 'draft' ? (
        <PreconditionChecklist
          isFP={isFP}
          precondition={precondition}
          preconditionError={preconditionError}
          onRefresh={() => {
            void qc.invalidateQueries({ queryKey: qk.agreement(agreement.id) });
            void qc.invalidateQueries({
              queryKey: qk.agreementAssignments(agreement.id),
            });
            void qc.invalidateQueries({
              queryKey: qk.agreementMilestones(agreement.id),
            });
          }}
        />
      ) : null}

      <div className="transition-row" role="group" aria-label="Available transitions">
        {activate.rendering !== 'hidden' ? (
          <button
            type="button"
            className="btn btn-primary"
            disabled={
              activate.rendering === 'disabled' ||
              !activatePreconditionsKnown ||
              activateBlocked ||
              preconditionError
            }
            aria-disabled={
              activate.rendering === 'disabled' ||
              !activatePreconditionsKnown ||
              activateBlocked
                ? 'true'
                : undefined
            }
            title={
              activate.rendering === 'disabled' ? activate.tooltip : activateTooltip
            }
            onClick={() => void handleActivateClick()}
          >
            Activate
          </button>
        ) : null}
        {complete.rendering !== 'hidden' ? (
          <button
            type="button"
            className="btn btn-primary"
            disabled={complete.rendering === 'disabled'}
            aria-disabled={complete.rendering === 'disabled' ? 'true' : undefined}
            title={complete.tooltip}
            onClick={() => setConfirmOpen('complete')}
          >
            Complete
          </button>
        ) : null}
        {revert.rendering !== 'hidden' ? (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setConfirmOpen('revert')}
          >
            Revert to Active
          </button>
        ) : null}
        {archive.rendering !== 'hidden' ? (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setConfirmOpen('archive')}
          >
            Archive
          </button>
        ) : null}
        {amend.rendering !== 'hidden' ? (
          <button
            type="button"
            className="btn btn-secondary"
            disabled={amend.rendering === 'disabled'}
            aria-disabled={amend.rendering === 'disabled' ? 'true' : undefined}
            title={amend.tooltip}
            onClick={() => navigate(`/agreements/${agreement.id}/amend`)}
          >
            Amend
          </button>
        ) : null}
      </div>

      <ConfirmationDialog
        open={confirmOpen === 'complete'}
        title="Complete this agreement?"
        body="The agreement transitions to Completed. Active assignments remain editable for end-date by an Admin until they are end-dated."
        confirmLabel="Complete agreement"
        confirmIntent="transition"
        onConfirm={() => performTransition('complete')}
        onCancel={() => {
          setConfirmOpen(null);
          setConfirmError(null);
          setStateRaceMessage(null);
        }}
        stateRaceMessage={confirmOpen === 'complete' ? stateRaceMessage : null}
        errorMessage={confirmOpen === 'complete' ? confirmError : null}
      />
      <ConfirmationDialog
        open={confirmOpen === 'revert'}
        title="Revert to Active?"
        body="The agreement returns to Active. Edit affordances are restored."
        confirmLabel="Revert agreement"
        confirmIntent="transition"
        onConfirm={() => performTransition('revert')}
        onCancel={() => {
          setConfirmOpen(null);
          setConfirmError(null);
          setStateRaceMessage(null);
        }}
        stateRaceMessage={confirmOpen === 'revert' ? stateRaceMessage : null}
        errorMessage={confirmOpen === 'revert' ? confirmError : null}
      />
      <ConfirmationDialog
        open={confirmOpen === 'archive'}
        title="Archive this agreement?"
        body="Archived agreements are read-only and don't appear in the default Agreement List filter. This cannot be reversed."
        confirmLabel="Archive agreement"
        confirmIntent="destructive"
        onConfirm={() => performTransition('archive')}
        onCancel={() => {
          setConfirmOpen(null);
          setConfirmError(null);
          setStateRaceMessage(null);
        }}
        stateRaceMessage={confirmOpen === 'archive' ? stateRaceMessage : null}
        errorMessage={confirmOpen === 'archive' ? confirmError : null}
      />

      {activationError ? (
        <ActivationBlockersModal
          error={activationError}
          onClose={() => setActivationError(null)}
        />
      ) : null}
      {archiveError ? (
        <ArchiveBlockersModal
          error={archiveError}
          onClose={() => setArchiveError(null)}
        />
      ) : null}
    </section>
  );
}

function PreconditionChecklist({
  isFP,
  precondition,
  preconditionError,
  onRefresh,
}: {
  isFP: boolean;
  precondition: PreconditionState;
  preconditionError: boolean;
  onRefresh: () => void;
}) {
  if (preconditionError) {
    return (
      <div className="inline-error" role="alert">
        Couldn't compute activation requirements.{' '}
        <button type="button" className="btn-link" onClick={onRefresh}>
          Refresh
        </button>
      </div>
    );
  }
  const items: Array<{ key: string; label: string; state: 'pending' | 'incomplete' | 'complete' }> =
    [
      {
        key: 'has_assignments',
        label: '≥ 1 Assignment exists',
        state: precondition.has_assignments ? 'complete' : 'incomplete',
      },
      {
        key: 'sales_credit_complete',
        label: 'Sales Credit = 100% on every assignment',
        state:
          precondition.sales_credit_complete === null
            ? 'pending'
            : precondition.sales_credit_complete
              ? 'complete'
              : 'incomplete',
      },
    ];
  if (isFP) {
    items.push({
      key: 'payment_schedule_within_tolerance',
      label: 'Payment schedule total within $100 of Total Value',
      state:
        precondition.payment_schedule_within_tolerance === null
          ? 'pending'
          : precondition.payment_schedule_within_tolerance
            ? 'complete'
            : 'incomplete',
    });
  }
  return (
    <ul className="precondition-list" role="list" aria-label="Activation requirements">
      {items.map((item) => (
        <li
          key={item.key}
          role="checkbox"
          aria-checked={
            item.state === 'pending'
              ? 'mixed'
              : item.state === 'complete'
                ? 'true'
                : 'false'
          }
        >
          <span className="pc-icon" aria-hidden="true">
            {item.state === 'pending' ? '…' : item.state === 'complete' ? '✓' : '✗'}
          </span>
          <span>
            {item.label}
            {item.state === 'pending' ? (
              <span className="visually-hidden"> — computing…</span>
            ) : null}
          </span>
        </li>
      ))}
    </ul>
  );
}
